import { observer } from "mobx-react-lite";
import {
  LoginPage,
  ViewStorePage,
  ProductsStorePage,
  ProductsStoreAddNewProduct,
  ProductsEditProduct,
  ProductPage,
  AccountDetails,
  AccountAddress,
  AccountPayment,
  AccountOrdersTable,
  CartPage,
  CheckoutPage,
  StatisticsPage,
  ActionHistory,
  DepartmentPage,
} from "pages";
import { useEffect, useMemo } from "react";
import { withAlert } from "react-alert";
import { Router as ReactRouter, Switch, Route } from "react-router";
import { history } from "mobx-stores";
import { MyStorePaths, RouterProps } from "./router.types";
import { MainContainer } from "@touche-lts/panel-ui/dist";
import { PrivateRoute } from "./private-route";
import {
  DialogPopupHandler,
  Footer,
  Header,
  LoadingSpinner,
  SideMenu
} from 'components'
import { useRootStore } from 'mobx-stores/store-provider'
import { OrdersPage } from 'pages/orders'
import { StorePage, StoresPage } from 'pages/stores'
import { User } from 'services'
import { UserRoleName } from 'models'
import cl from 'classnames'
import styles from './router.module.scss'
import { HomePage } from 'pages/home'
import { DepartmentCategoryPage } from 'pages/departments/department-category'
import { TermsPage } from 'pages/terms'

const RouterComponent = (props: RouterProps) => {
  const { alert } = props;

  const {
    generalStore: { alert: alertData },
    routingStore: {
      location: { pathname },
    },
    userStore: { assignedStore: store },
    departmentsStore: { getAllDepartments },
  } = useRootStore();

  const isTempUser = JSON.parse(localStorage.getItem('isTempUser'));
  const isUserDetails = JSON.parse(localStorage.getItem('userDetails'));
  const isStoreAdmin = User.isStoreAdmin;
  const storeAdminRoles = [UserRoleName.STORE_ADMIN, UserRoleName.SUPER_ADMIN];

  useEffect(() => {
    if (alertData.counter > 0) {
      alert[alertData.type](alertData.message);
    }
    if (!store) {
      getAllDepartments();
    }
    // eslint-disable-next-line
  }, [alertData.counter, getAllDepartments]);

  const visibleHeader = pathname !== "/login";
  const visibleSideMenu = visibleHeader && User.isAuthenticated;
  const storesView = !User.isAuthenticated ? styles.storeContainer : "";

  const visibleFooter = useMemo(() => {
    return !store && !isUserDetails;
  }, [store, isUserDetails]);

  document.getElementById("root").style.overflowX = "hidden";
  document.getElementById("root").className = "thinScroll"; // For thin scroll

  switch (pathname) {
    case MyStorePaths.STORES:
    case MyStorePaths.DEPARTMENTS:
      document.getElementById("root").style.overflowY = "auto";
      break;

    default:
      break;
  }

  const isNoHeaderPages = useMemo(() => {
    const noHeaderPages = ["/login", "/terms"];
    return noHeaderPages.includes(pathname);
  }, [pathname]);

  return (
    <ReactRouter history={history}>
      {/* Header */}
      {visibleHeader && <Header />}

      {/* Main container */}
      <MainContainer
        className={cl(
          storesView,
          styles.mainContainer,
          isNoHeaderPages && styles.noHeaderContainer,
        )}
      >
        {visibleSideMenu && !isTempUser && <SideMenu />}
        <Switch>
          {/* Public routes and some private routes */}
          <Route path='/login' component={LoginPage} />
          <Route path='/terms' component={TermsPage} />
          <PrivateRoute
            exact
            path='/'
            component={ViewStorePage}
          />
          { // Available only for regular user and not for admin
            !store && !isStoreAdmin &&(
              <>
                {/* <Route exact path='/' component={HomePage} />  */}
                {!User.isAuthenticated ? (
                  <Route exact path="/" component={HomePage} />
                ) : (
                  <PrivateRoute exact path="/" component={StoresPage} />
                )}
                {!User.isAuthenticated ? (
                  <Route
                    exact
                    path={MyStorePaths.STORES}
                    component={StoresPage}
                  />
                ) : (
                  <PrivateRoute
                    exact
                    path={MyStorePaths.STORES}
                    component={StoresPage}
                  />
                )}
                {!User.isAuthenticated ? (
                  <Route
                    exact
                    path={MyStorePaths.STOREVIEW}
                    component={StorePage}
                  />
                ) : (
                  <PrivateRoute
                    exact
                    path={MyStorePaths.STOREVIEW}
                    component={StorePage}
                  />
                )}

                {!User.isAuthenticated ? (
                  <Route
                    exact
                    path={MyStorePaths.PRODUCT}
                    component={ProductPage}
                  />
                ) : (
                  <PrivateRoute
                    exact
                    path={MyStorePaths.PRODUCT}
                    component={ProductPage}
                  />
                )}
                {!User.isAuthenticated ? (
                  <Route exact path={MyStorePaths.CART} component={CartPage} />
                ) : (
                  <PrivateRoute
                    exact
                    path={MyStorePaths.CART}
                    component={CartPage}
                  />
                )}
                {!User.isAuthenticated ? (
                  <Route
                    exact
                    path={MyStorePaths.DEPARTMENTS}
                    component={DepartmentPage}
                  />
                ) : (
                  <PrivateRoute
                    exact
                    path={MyStorePaths.DEPARTMENTS}
                    component={DepartmentPage}
                  />
                )}
                {!User.isAuthenticated ? (
                  <Route
                    exact
                    path={MyStorePaths.DEPARTMENT_CATEGORY}
                    component={DepartmentPage}
                  />
                ) : (
                  <PrivateRoute
                    exact
                    path={MyStorePaths.DEPARTMENT_CATEGORY}
                    component={DepartmentPage}
                  />
                )}
                {!User.isAuthenticated ? (
                  <Route
                    exact
                    path={MyStorePaths.DEPARTMENT_CATEGORY_FILTER}
                    component={DepartmentCategoryPage}
                  />
                ) : (
                  <PrivateRoute
                    exact
                    path={MyStorePaths.DEPARTMENT_CATEGORY_FILTER}
                    component={DepartmentCategoryPage}
                  />
                )}
                <PrivateRoute
                  exact
                  path={MyStorePaths.ACCOUNT_DETAILS}
                  component={AccountDetails}
                />
                <PrivateRoute
                  exact
                  path={MyStorePaths.ACCOUNT_ADDRESSES}
                  component={AccountAddress}
                />
                <PrivateRoute
                  exact
                  path={MyStorePaths.ACCOUNT_PAYMENT}
                  component={AccountPayment}
                />
                <PrivateRoute
                  exact
                  path={MyStorePaths.ACCOUNT_ORDERS}
                  component={AccountOrdersTable}
                />
                <PrivateRoute
                  exact
                  path={MyStorePaths.CHECKOUT}
                  component={CheckoutPage}
                />
              </>
            )
          }

          {/* Private routes */}

          <PrivateRoute
            exact
            path={MyStorePaths.PRODUCT_ADD_BY_STORE}
            component={ProductsStoreAddNewProduct}
          />
          <PrivateRoute
            exact
            path={MyStorePaths.PRODUCT_VIEW_BY_STORE}
            component={ProductsEditProduct}
          />
          <PrivateRoute
            exact
            path={MyStorePaths.PRODUCTS_BY_STORE}
            component={ProductsStorePage}
          />
          <PrivateRoute
            exact
            path={MyStorePaths.STATISTICS_BY_STORE}
            component={StatisticsPage}
          />
          <PrivateRoute
            exact
            path={MyStorePaths.ROOT_BY_STORE}
            component={ViewStorePage}
          />
          <PrivateRoute
            exact
            path={MyStorePaths.ROOT}
            component={ViewStorePage}
            allowedRoles={storeAdminRoles}
          />
          <PrivateRoute
            exact
            path={MyStorePaths.DEPARTMENTS}
            component={DepartmentPage}
          />
          <PrivateRoute
            exact
            path={MyStorePaths.DEPARTMENT_CATEGORY_FILTER}
            component={DepartmentCategoryPage}
          />
          <PrivateRoute
            exact
            path={MyStorePaths.STOREVIEW}
            component={StorePage}
          />
          <PrivateRoute
            exact
            path={MyStorePaths.PRODUCT}
            component={ProductPage}
          />
          <PrivateRoute
            exact
            path={MyStorePaths.DEPARTMENT_CATEGORY}
            component={DepartmentPage}
          />
          <PrivateRoute
            exact
            path={MyStorePaths.ACCOUNT_DETAILS}
            component={AccountDetails}
          />
          <PrivateRoute
            exact
            path={MyStorePaths.STORES}
            component={StoresPage}
          />
          <PrivateRoute
            exact
            path={MyStorePaths.PRODUCTS}
            component={ProductsStorePage}
            allowedRoles={storeAdminRoles}
          />
          <PrivateRoute
            exact
            path={MyStorePaths.PRODUCT_ADD}
            component={ProductsStoreAddNewProduct}
            allowedRoles={storeAdminRoles}
          />
          <PrivateRoute
            exact
            path={MyStorePaths.PRODUCT_VIEW}
            component={ProductsEditProduct}
            allowedRoles={storeAdminRoles}
          />
          <PrivateRoute
            exact
            path={MyStorePaths.ORDERS}
            component={OrdersPage}
            allowedRoles={storeAdminRoles}
          />
          <PrivateRoute
            exact
            path={MyStorePaths.STATISTICS}
            component={StatisticsPage}
            allowedRoles={storeAdminRoles}
          />
          <PrivateRoute
            exact
            path={MyStorePaths.ACTION_HISTORY}
            component={ActionHistory}
            allowedRoles={storeAdminRoles}
          />
        </Switch>
      </MainContainer>

      {/* Footer */}
      {visibleFooter && <Footer />}

      {/* Loading spinner */}
      <LoadingSpinner />

      {/* Dialog popup handler */}
      <DialogPopupHandler />
    </ReactRouter>
  );
};

const Router = withAlert()(observer(RouterComponent));
export { Router };
